import {
  MEDIA_TYPE_LABELS,
  type MediaItem,
  MediaList,
  ORIENTATION_LABELS,
  type MediaOrientation,
  type MediaType,
} from '@deizi/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { z } from 'zod';
import { api } from './api';
import { formatBytes } from './backups-api';
import { productsKey } from './products-api';

export const mediaKey = ['media'] as const;

const busy = (data: z.infer<typeof MediaList> | undefined) =>
  data?.data.some((m) => m.status === 'queued' || m.status === 'processing') ?? false;

/** A product's gallery; polls while photos or videos are still being processed. */
export function useProductMedia(productId: string) {
  return useQuery({
    queryKey: [...mediaKey, 'product', productId],
    queryFn: () => api.get(`/products/${productId}/media`, MediaList),
    refetchInterval: (q) => (busy(q.state.data) ? 2500 : false),
  });
}

export interface LibraryFilters {
  type: MediaType | '';
  orientation: MediaOrientation | '';
  kind: 'photo' | 'video' | '';
  productId: string;
  notPosted: boolean;
  approved: 'social' | 'marketplace' | '';
}

export function useMediaLibrary(f: LibraryFilters) {
  const params = new URLSearchParams({ limit: '200', sort: 'recent' });
  if (f.type) params.set('type', f.type);
  if (f.orientation) params.set('orientation', f.orientation);
  if (f.kind) params.set('kind', f.kind);
  if (f.productId) params.set('productId', f.productId);
  if (f.notPosted) params.set('notPosted', 'true');
  if (f.approved) params.set('approved', f.approved);
  return useQuery({
    queryKey: [...mediaKey, 'library', params.toString()],
    queryFn: () => api.get(`/media?${params.toString()}`, MediaList),
    placeholderData: (prev) => prev,
    refetchInterval: (q) => (busy(q.state.data) ? 2500 : false),
  });
}

/** Refreshes galleries, the library and the product (tab counts, cover). */
export function useInvalidateMedia() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: mediaKey });
    void qc.invalidateQueries({ queryKey: productsKey });
  };
}

/** "0:42", "1:05:10" */
export function formatClip(seconds: number): string {
  const s = Math.round(seconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, '0');
  return h ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** "Feed 4:5 · 1080 × 1350" */
export function formatShape(m: MediaItem): string {
  if (!m.width || !m.height || !m.orientation) return '—';
  return `${ORIENTATION_LABELS[m.orientation]} · ${m.width} × ${m.height}`;
}

/** Title in the grid and the detail: "Timelapse", "Foto · Produto"… */
export function mediaTitle(m: MediaItem): string {
  const type = MEDIA_TYPE_LABELS[m.type];
  if (m.type === 'timelapse' || m.type === 'making_of') return type;
  return `${m.kind === 'video' ? 'Vídeo' : 'Foto'} · ${type}`;
}

/** "JPG · convertido de HEIC (3,4 MB)" or "prévia comprimida 4,1 MB · original 38 MB intacto". */
export function mediaNote(m: MediaItem): string {
  if (m.kind === 'video') {
    const parts = [];
    if (m.previewBytes !== null) parts.push(`prévia comprimida ${formatBytes(m.previewBytes)}`);
    parts.push(
      m.originalPruned ? 'original apagado' : `original ${formatBytes(m.originalBytes)} intacto`,
    );
    return parts.join(' · ');
  }
  return m.convertedFrom
    ? `JPG · convertido de ${m.convertedFrom} (${formatBytes(m.originalBytes)})`
    : `${m.name} · ${formatBytes(m.originalBytes)}`;
}

/** Accepted by the media uploads (same list as the API's FILE_LIMITS.media). */
export const MEDIA_ACCEPT = '.jpg,.jpeg,.png,.webp,.heic,.heif,.mp4,.mov,.m4v,image/*,video/*';
